import { type Editor } from "@tiptap/react";
import { List, ListOrdered } from "lucide-react";

import { cn, getText } from "shared/lib";

import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "shared/components";

export interface EditorToolbarProps {
  disabled?: boolean;
  editor: Editor | undefined | null;
}

export function EditorToolbar({ disabled, editor }: EditorToolbarProps) {
  if (!editor) {
    return null;
  }

  return (
    <div className="bg-interfaceBgLight border-interactiveBorder flex items-center gap-3 rounded-t border-b px-1">
      <Select
        disabled={disabled}
        onValueChange={(value) => {
          if (value === "paragraph") {
            editor.chain().focus().setParagraph().run();
          } else if (value === "heading") {
            editor.chain().focus().toggleHeading({ level: 1 }).run();
          }
        }}
        value={editor.isActive("heading") ? "heading" : "paragraph"}
      >
        <SelectTrigger className="bg-interfaceBgLight my-0 h-fit w-fit rounded-none border-0 py-0">
          <SelectValue placeholder={getText("Text format")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="paragraph">{getText("Normal text")}</SelectItem>
          <SelectItem value="heading">{getText("Heading")}</SelectItem>
        </SelectContent>
      </Select>

      <div className="bg-interactiveBorder w-[1px]">&nbsp;</div>

      <Button
        className={cn(
          "my-0 ml-0 mr-0 h-fit px-2 py-1",
          editor.isActive("bulletList") ? "bg-actionBgSelect" : "",
        )}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        variant="ghost"
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        className={cn(
          "my-0 ml-0 mr-0 h-fit px-2 py-1",
          editor.isActive("orderedList") ? "bg-actionBgSelect" : "",
        )}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        variant="ghost"
      >
        <ListOrdered className="h-4 w-4" />
      </Button>

      <div className="bg-interactiveBorder w-[1px]">&nbsp;</div>

      <Button
        className="my-0 ml-0 mr-0 h-fit px-2 py-1"
        disabled={disabled || !editor.can().chain().focus().undo().run()}
        onClick={() => editor.chain().focus().undo().run()}
        variant="ghost"
      >
        {getText("Undo")}
      </Button>
      <Button
        className="my-0 ml-0 mr-0 h-fit px-2 py-1"
        disabled={disabled || !editor.can().chain().focus().redo().run()}
        onClick={() => editor.chain().focus().redo().run()}
        variant="ghost"
      >
        {getText("Redo")}
      </Button>
    </div>
  );
}
